import { useQuery } from '@tanstack/react-query';
import { LocaleLink as Link } from '../i18n/locale-link';
import { useLocale, useT } from '../i18n/locale-context';
import { getBaseRates } from '../lib/catalog-client';
import { DEGENERATE_MODE_SHARE_PCT } from '../lib/metric-thresholds';
import { formatBudgetCompact, formatDateNumeric, pluralRuIndex } from '../lib/format';
import { countryConfig } from '../country';
import { useHydrated } from '../lib/use-hydrated';
import { AnalyticsTeaser } from './analytics-teaser';
import { MetricCell, MetricGrid, PriceBandBar } from './analytics-card-parts';
import { MarketTeaserBody } from './analytics-teaser-bodies';
import { InfoHint } from './info-hint';

/**
 * Base-rates analytics panel for a tender's detail page — aggregate market
 * signal only (median discount/participants, winning-price band). Never
 * renders any individual winner name/ИНН.
 *
 * Hide-when-empty (Phase 5): the response carries a non-sensitive `hasData`
 * existence flag. The panel renders NOTHING when there's no data behind the
 * wall (`!hasData`) — no dead upsell on the tenders with no comparable
 * outcomes. `locked && hasData` → the locked teaser; unlocked data → the
 * panel. Guests see the teaser too (the endpoint is public and answers them
 * with the locked payload) — it is one of the arguments for registering.
 *
 * ⚠️ **The DISCOUNT leads, not the absolute price** (2026-09-14). A median
 * winning price is barely meaningful across lots of different sizes; the
 * discount from the starting price is comparable at any size and is what a
 * supplier turns into margin. The price stays, one tier down, and always says
 * what population it is a median OF — because until the budget band landed, the
 * answer was «вся ОКЭД-секция», where 30 M lots sit beside 5 bn ones. Anything
 * that renders the price must render its basis line with it.
 */

/** The three Russian plural forms, in the order `pluralRuIndex` returns. */
const PLURAL_FORMS = ['one', 'few', 'many'] as const;

/** Length of the comparables' award window, in whole months (at least 1) — the
 *  locked teaser says «за 12 месяцев» instead of printing the ISO dates the
 *  server sends. Averaged month length, because a window is a span, not a
 *  calendar difference. */
export function windowMonths(from: string, to: string): number {
  const a = Date.parse(from);
  const b = Date.parse(to);
  if (!Number.isFinite(a) || !Number.isFinite(b) || b < a) return 1;
  return Math.max(1, Math.round((b - a) / (30.44 * 24 * 3600 * 1000)));
}

/**
 * Kazakhstan only (2026-10-15 phone pass), so that uztender.com renders exactly
 * as before:
 *  - the basis line's award window reads «11.08.2026 – 30.09.2026», not the
 *    ISO «2026-08-11 – 2026-09-30» the server sends (a dotted date needs no
 *    locale data, which Chrome lacks for `kk` as it does for `uz`);
 *  - «Медиана участников» is not drawn at all when the comparables carry no
 *    participant counts — every Kazakh result — instead of a cell reading «—».
 * The Uzbek page still prints ISO dates and the «—»; both look like defects
 * there too, but changing them is the Uzbek owner's call.
 */
const KZ_PANEL_POLISH = countryConfig.code !== 'uz';

/** The award-window date as the basis line prints it — see `KZ_PANEL_POLISH`. */
function windowDate(iso: string): string {
  return KZ_PANEL_POLISH ? (formatDateNumeric(iso) ?? iso) : iso;
}

export function BaseRatesPanel({ tenderId }: { tenderId: string }) {
  const t = useT();
  const { locale } = useLocale();
  // NO auth gate: the route is public since 2026-09-18 and answers a guest with
  // the LOCKED payload. A visitor who has not registered is exactly the person
  // the locked card is arguing at, so hiding it from them was backwards.
  const hydrated = useHydrated();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['base-rates', tenderId],
    queryFn: () => getBaseRates(tenderId),
    // ⚠️ BROWSER ONLY. The prerender renders this component on ~5k tender pages
    // × 2 locales; letting these three queries run there would add ~30k API
    // calls per run — including the buyer-graph union — to a 4-core host that
    // is also serving live traffic. `useHydrated()` is false during SSR and
    // during hydration, so the card is fetched after the page is interactive
    // and never by the prerenderer. (react-query does not fetch in
    // `renderToString` either; this makes that guarantee explicit and greppable
    // instead of relying on it.)
    enabled: hydrated,
  });

  /** The locked card: the real layout with blurred values, plus ONE true line
   *  built from the server's non-decision counts («По 500 сравнимым закупкам,
   *  2025-09-18 – 2026-09-17»). The counts are optional on the wire, so an API
   *  one deploy behind simply drops the line rather than the card. */
  const teaser = (note?: string, metrics?: { participants: boolean; price: boolean }) => (
    <AnalyticsTeaser
      wall="winPrices"
      tenderId={tenderId}
      title={t('baseRates.title')}
      hint={t('baseRates.hint')}
      icon={
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="h-4 w-4"
          aria-hidden
        >
          <polyline points="3 17 9 11 13 15 21 7" />
          <polyline points="15 7 21 7 21 13" />
        </svg>
      }
      note={note}
    >
      <MarketTeaserBody metrics={metrics} />
    </AnalyticsTeaser>
  );

  if (isLoading) {
    return (
      <section
        className="animate-pulse rounded-lg border border-edge bg-bone-surface p-5 shadow-m-sm sm:p-6"
        aria-hidden
      >
        <div className="h-4 w-32 rounded bg-bone" />
        <div className="mt-3 h-16 rounded bg-bone" />
      </section>
    );
  }

  if (isError || !data) return null;

  // Unlocked with data → always show it. The server only sends `rates` to an
  // entitled viewer, so the presence of data is itself the entitlement proof;
  // don't let a missing/false `hasData` existence-flag hide real analytics
  // (robust to an API version that predates the flag).
  const r = !data.locked ? data.rates : null;

  // No renderable data: surface the upgrade CTA only when data exists behind the
  // wall (`locked && hasData`) — never a dead upsell — else render nothing.
  if (!r) {
    if (!(data.locked && data.hasData !== false)) return null;
    const n = data.locked ? data.comparableCount : undefined;
    const range = data.locked ? data.dateRange : null;
    // «за 12 месяцев», not «2025-09-18 – 2026-09-17»: nothing else on the site
    // shows a reader an ISO date, and a window LENGTH is what the sentence is
    // actually about. No window on the wire → the line simply drops the period.
    const months = range ? windowMonths(range.from, range.to) : null;
    return teaser(
      n == null || n <= 0
        ? undefined
        : months
          ? t('baseRates.teaserBasis', {
              n,
              period: t(`baseRates.teaserMonths.${PLURAL_FORMS[pluralRuIndex(months)]}`, {
                n: months,
              }),
            })
          : t('baseRates.teaserBasisNoDates', { n }),
      // Kazakhstan only (`honestTeasers`): blur just the metrics that exist.
      countryConfig.features.honestTeasers && data.locked ? data.metrics : undefined,
    );
  }

  const wp = r.winningPriceUzs;
  const fillPct =
    wp && wp.p75 > wp.p25
      ? Math.min(100, Math.max(0, ((wp.median - wp.p25) / (wp.p75 - wp.p25)) * 100))
      : 50;

  // `banded` is optional on the wire (an older API omits it). `undefined` reads
  // as NOT banded — i.e. we print the cautious «размер лота не учитывается»
  // label rather than silently claiming a like-for-like comparison we can't
  // prove was made.
  const banded = r.banded === true && r.band != null;
  const compact = (n: number) => formatBudgetCompact(String(Math.round(n)), locale) ?? '—';

  // Basis line — how many comparables, over what size range, over what period.
  // This is what turns a bare median into a number the reader can audit.
  const basis = banded
    ? r.dateRange
      ? t('baseRates.basisBanded', {
          n: r.sample,
          min: compact(r.band!.minUzs),
          max: compact(r.band!.maxUzs),
          from: windowDate(r.dateRange.from),
          to: windowDate(r.dateRange.to),
        })
      : t('baseRates.basisBandedNoDates', {
          n: r.sample,
          min: compact(r.band!.minUzs),
          max: compact(r.band!.maxUzs),
        })
    : r.dateRange
      ? t('baseRates.basisSection', {
          n: r.sample,
          from: windowDate(r.dateRange.from),
          to: windowDate(r.dateRange.to),
        })
      : t('baseRates.basisSectionNoDates', { n: r.sample });

  // Literal `t('…')` calls on purpose: `keys-exist.test.ts` only sees literal
  // keys, so a computed `t(positionKey)` would ship untranslated in silence —
  // `translate()` returns the key itself when it can't resolve one.
  const positionLine =
    r.budgetPosition === 'below'
      ? t('baseRates.positionBelow')
      : r.budgetPosition === 'above'
        ? t('baseRates.positionAbove')
        : r.budgetPosition === 'inside'
          ? t('baseRates.positionInside')
          : null;

  return (
    <section className="overflow-hidden rounded-lg border border-edge bg-bone-surface shadow-m-sm">
      {/* Header — icon, title + sample subtitle, PRO badge. */}
      <div className="flex items-center gap-2.5 p-4 sm:p-5">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-brand-dim text-brand">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-4 w-4"
            aria-hidden
          >
            <polyline points="3 17 9 11 13 15 21 7" />
            <polyline points="15 7 21 7 21 13" />
          </svg>
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <h3 className="text-[15px] font-bold text-ink">{t('baseRates.title')}</h3>
            <InfoHint text={t('baseRates.hint')} />
          </div>
          <p className="text-[12px] text-ink-muted">{basis}</p>
        </div>
      </div>

      {/* ── Скидка — ГЛАВНАЯ цифра. Она сопоставима между лотами любого размера,
             в отличие от абсолютной цены, и именно её поставщик превращает в
             маржу. Полная ширина, тир выше участников и цены. ─────────────── */}
      {r.discountPct && (
        <div className="border-t border-edge-light p-4 sm:p-5">
          <div className="mb-1 text-[11px] font-bold uppercase tracking-[0.05em] text-ink-muted">
            {t('baseRates.discount')}
          </div>
          <div className="flex items-center gap-1.5 text-[34px] font-bold leading-none tabular-nums text-ink">
            {`${r.discountPct.median}%`}
            {/* ⚠️ Both discount distributions are degenerate: tender-mc enforces a
                5% minimum bid step (88.3% of 77,836 rows land on exactly 5.00%)
                and etender competes on technical score, so the winning bid equals
                the start (99.1% of 3,007 rows at exactly 0.00%). The median is
                the platform's rulebook restated, and a supplier planning a bid
                around it is planning around a rule. Ride it on an InfoHint on the
                value rather than a long caption. */}
            {r.discountPct.modeSharePct >= DEGENERATE_MODE_SHARE_PCT && (
              <InfoHint text={t('baseRates.degenerate', { pct: r.discountPct.modeSharePct })} />
            )}
          </div>
          <p className="mt-1.5 text-[12px] text-ink-muted">{t('baseRates.discountCaption')}</p>
        </div>
      )}

      {/* Участников + медиана цены — secondary, divided cells. */}
      {(r.participants || wp) && (
        <MetricGrid single={KZ_PANEL_POLISH && !(r.participants && wp)}>
          {(r.participants || !KZ_PANEL_POLISH) && (
            <MetricCell
              label={t('baseRates.competition')}
              value={r.participants ? r.participants.median : '—'}
            />
          )}
          {/* `basis`: an absolute median means nothing without the population
              it is a median OF — say it on the same line as the number. */}
          {(wp || !KZ_PANEL_POLISH) && (
            <MetricCell
              label={t('baseRates.priceMedian')}
              value={wp ? compact(wp.median) : '—'}
              basis={banded ? t('baseRates.priceBasisBanded') : t('baseRates.priceBasisSection')}
            />
          )}
        </MetricGrid>
      )}

      {/* Цена победителя — gradient range bar with compact min/max. */}
      {/* `footer`: where THIS lot sits against that range — the one sentence a
          reader can act on, and it costs only the p25/p75 already computed. */}
      {wp && (
        <PriceBandBar
          label={t('baseRates.priceBand')}
          fillPct={fillPct}
          low={formatBudgetCompact(String(Math.round(wp.p25)), locale)}
          high={formatBudgetCompact(String(Math.round(wp.p75)), locale)}
          footer={positionLine && <p className="mt-2 text-[12px] text-ink-soft">{positionLine}</p>}
        />
      )}

      {/* No budget on the subject tender → the comparison ignored lot size.
          Say so rather than passing it off as like-for-like. */}
      {!banded && (
        <p className="border-t border-edge-light bg-bone px-4 py-3 text-[12px] leading-snug text-ink-muted sm:px-5">
          {t('baseRates.sizeBlind')}
        </p>
      )}

      {/* Deep link to the standalone, product-keyed price history (#46). */}
      <div className="border-t border-edge-light py-3 text-center">
        <Link
          to={`/prices/${encodeURIComponent(r.ikpu)}`}
          className="text-[13px] font-semibold text-brand hover:underline"
        >
          {t('baseRates.pricesLink')}
        </Link>
      </div>
    </section>
  );
}
